/**
 * **打断预示（全屏）。**
 *
 * 有人把 [断点] 拖向屏幕中心时，除他以外的每个人都会看到一张 [断点] 从
 * **斜上方**淡入、飞向屏幕中心（终点带一个确定性的随机偏移）。
 *
 * 只负责"画这一批正在进行的预示"：
 *
 * - `set(id, raw)`：给某个打断者喂一帧**行程**（0 = 刚按下，1 = 到中心，
 *   >1 = 拖过头）；同一个人反复喂就是实时跟随。
 * - 松手 = 喂 0（`clear` 只是 `set(id, 0)` 的写法）：牌**反向收回**淡出。
 * - `entryAngles` / `endpointJitter` 都是纯函数，见 `interruptIntent.ts`。
 *
 * ⚠️ 这一层**不知道网络**，也不知道谁是"自己"：调用方负责过滤发送者。
 */

import { Container, type Renderer, type Ticker } from 'pixi.js';
import type { CardRegistry } from '../../core/pack';
import { isInterrupt, type CardDef } from '../../core/card';
import { faceSize } from '../card/metrics';
import type { CardFaceCache } from '../card/faceCache';
import { CardActor } from './cardActor';
import { easeOut } from '../ui/motion';
import {
  ENDPOINT_RADIUS_SHARE,
  endpointJitter,
  entryAngles,
  interruptOpacity,
  interruptProgress,
} from './interruptIntent';

/** 入场点在画布对角线外多远（一个够大的数，保证从屏幕外进来） */
const ENTRY_RADIUS_SHARE = 0.78;
/** 行程逼近期望值的速度（1/秒）。越大越跟手 */
const FOLLOW_RATE = 14;
/** 低于这个可见度就把这一张摘掉 */
const CULL_BELOW = 0.01;

export interface InterruptRiftOptions {
  readonly renderer: Renderer;
  readonly registry: CardRegistry;
  /** 共享牌面缓存（缩略牌统一走它，别各自画） */
  readonly faces: CardFaceCache;
  readonly bounds: { readonly width: number; readonly height: number };
  /** [断点] 画多高（逻辑像素）。默认 108 —— 比手牌大，因为是全屏预示 */
  readonly height?: number;
}

interface Rift {
  readonly view: Container;
  /** 一张 [断点] —— 复用 `CardActor`（`CardView` + `CardRig`） */
  readonly actor: CardActor;
  /** 期望行程（发送端给的原值） */
  raw: number;
  /** 平滑之后的行程 */
  shown: number;
  angle: number;
  jitter: { x: number; y: number; rotation: number };
}

export class InterruptRift {
  readonly view: Container;

  readonly #size: ReturnType<typeof faceSize>;
  readonly #faces: CardFaceCache;
  readonly #def: CardDef | null;
  #bounds: { width: number; height: number };
  readonly #radius: number;
  readonly #entryRadius: number;

  readonly #active = new Map<string, Rift>();

  constructor(options: InterruptRiftOptions) {
    const def = options.registry.filter(isInterrupt)[0] ?? null;
    this.#def = def;
    this.#faces = options.faces;
    this.#bounds = { ...options.bounds };
    this.#size = faceSize(options.height ?? 108);
    const short = Math.min(this.#bounds.width, this.#bounds.height);
    this.#radius = short * ENDPOINT_RADIUS_SHARE;
    this.#entryRadius = Math.hypot(this.#bounds.width, this.#bounds.height) * ENTRY_RADIUS_SHARE;

    this.view = new Container();
    this.view.label = 'interrupt-rift';
    this.view.eventMode = 'none';
    void options.renderer;
  }

  setBounds(bounds: { readonly width: number; readonly height: number }): void {
    this.#bounds = { ...bounds };
  }

  /** **喂一帧行程。** `raw <= 0` 且该 id 不在场时什么都不做（不凭空出现） */
  set(id: string, raw: number): void {
    const def = this.#def;
    if (def === null) return;
    const value = Number.isFinite(raw) && raw > 0 ? raw : 0;
    let rift = this.#active.get(id);
    if (rift === undefined) {
      if (value <= 0) return;
      rift = this.#spawn(def);
      this.#active.set(id, rift);
      this.#reassignAngles();
    }
    rift.raw = value;
  }

  /** 松手 / 取消：反向收回（等价于喂 0） */
  clear(id: string): void {
    const rift = this.#active.get(id);
    if (rift !== undefined) rift.raw = 0;
  }

  update(ticker: Ticker): void {
    if (this.#active.size === 0) return;
    const dt = Math.min(ticker.deltaMS / 1000, 0.1);
    const k = 1 - Math.exp(-FOLLOW_RATE * dt);
    const cx = this.#bounds.width / 2;
    const cy = this.#bounds.height / 2;

    for (const [id, rift] of [...this.#active]) {
      rift.shown += (rift.raw - rift.shown) * k;
      if (rift.raw === 0 && rift.shown < CULL_BELOW) {
        this.#active.delete(id);
        rift.actor.destroy();
        rift.view.destroy();
        continue;
      }
      if (rift.raw > 0 && rift.shown < 1e-4) rift.shown = rift.raw;

      const p = interruptProgress(rift.shown);
      const alpha = interruptOpacity(rift.shown);
      // 位置：p ∈ [0,1] 走 ease-out；>1 渐近到终点（对数已经把它压得很慢）
      const travel = p <= 1 ? easeOut(p) : 1 - 1 / (1 + (p - 1) * 4);
      const ex = cx + Math.cos(rift.angle) * this.#entryRadius;
      const ey = cy + Math.sin(rift.angle) * this.#entryRadius;
      const tx = cx + rift.jitter.x;
      const ty = cy + rift.jitter.y;
      rift.view.position.set(ex + (tx - ex) * travel, ey + (ty - ey) * travel);
      rift.view.rotation = rift.jitter.rotation * travel;
      rift.view.alpha = alpha;
      // 越拖越"实"一点：0.85 → 1 倍
      const scale = 0.85 + 0.15 * Math.min(1, Math.max(0, p));
      rift.view.scale.set(scale);
      // 复用 `CardActor` 的 idle / hover 手感
      rift.actor.update(ticker.deltaMS);
    }
  }

  /** 当前在场的人数（诊断 / 测试用） */
  get activeCount(): number {
    return this.#active.size;
  }

  destroy(): void {
    for (const rift of this.#active.values()) {
      rift.actor.destroy();
      rift.view.destroy();
    }
    this.#active.clear();
    this.view.destroy({ children: true });
  }

  #spawn(def: CardDef): Rift {
    // ★ 复用 `CardActor`：一张缩略牌该有的贴图 / 裁切 / 动效都在里面
    const actor = new CardActor({
      faces: this.#faces,
      def,
      height: this.#size.height,
    });
    // `CardActor.view` 原点在左上角 → 挪到牌心，外层容器才是绕中心转 / 缩放
    actor.view.position.set(-this.#size.width / 2, -this.#size.height / 2);
    const view = new Container();
    view.addChild(actor.view);
    view.position.set(this.#bounds.width / 2, this.#bounds.height / 2);
    this.view.addChild(view);
    return {
      view,
      actor,
      raw: 0,
      shown: 0,
      angle: 0,
      jitter: endpointJitter('', this.#radius),
    };
  }

  #reassignAngles(): void {
    const angles = entryAngles([...this.#active.keys()]);
    for (const [id, rift] of this.#active) {
      rift.angle = angles.get(id) ?? 0;
      rift.jitter = endpointJitter(id, this.#radius);
    }
  }
}
